/**
 * Total-partition grouping for the question navigator's "Group by"
 * mode — v2-native counterpart of v1 desktop's `groupQuestionsByTagValue`
 * (src/utils/auditTagUtils.ts). Reads the SDK's nested
 * `questionDetails.tagsIncluded` shape and returns untagged questions
 * separately so the component can localize the "Ungrouped" header.
 */

// Structural subset (same pattern as matchesQuestionFilters) so this
// file doesn't couple to the SDK's exact shape.
interface QuestionForGrouping {
  questionDetails?:
    | {
        tagsIncluded?: Record<string, readonly string[]> | undefined;
      }
    | undefined;
}

interface QuestionForAnsweredCount {
  responseDetails?:
    | {
        mainQuestionResponse?:
          | {
              responseResult?: string | undefined;
            }
          | undefined;
      }
    | undefined;
}

export interface QuestionTagGroupBucket<Q> {
  readonly tagValue: string;
  readonly questions: readonly Q[];
}

export interface GroupedQuestions<Q> {
  readonly groups: readonly QuestionTagGroupBucket<Q>[];
  readonly ungrouped: readonly Q[];
}

const COLLATOR = new Intl.Collator(undefined, { sensitivity: 'base' });

function tagValuesFor(
  question: QuestionForGrouping,
  tagGroupName: string,
): readonly string[] {
  const tagRecord = question.questionDetails?.tagsIncluded;
  if (!tagRecord) return [];
  // Guarded lookup — never dynamic-index a record with a raw string
  // (prototype-shadowed keys like `__proto__` would slip through).
  if (!Object.prototype.hasOwnProperty.call(tagRecord, tagGroupName)) return [];
  // eslint-disable-next-line security/detect-object-injection -- guarded by hasOwnProperty above
  return tagRecord[tagGroupName] ?? [];
}

/**
 * Buckets `questions` by each value of `tagGroupName`, sorted by
 * locale-aware collation. A question carrying multiple values appears
 * in EVERY matching bucket; questions without the tag group land in
 * `ungrouped`. No question is ever dropped — grouping reorganizes,
 * it never filters.
 */
export function groupQuestionsByTagGroup<Q extends QuestionForGrouping>(
  questions: readonly Q[],
  tagGroupName: string,
): GroupedQuestions<Q> {
  const buckets = new Map<string, Q[]>();
  const ungrouped: Q[] = [];

  for (const question of questions) {
    const values = tagValuesFor(question, tagGroupName);
    if (values.length === 0) {
      ungrouped.push(question);
      continue;
    }
    for (const value of values) {
      const bucket = buckets.get(value);
      if (bucket) {
        bucket.push(question);
      } else {
        buckets.set(value, [question]);
      }
    }
  }

  const groups = Array.from(buckets.entries())
    .sort(([a], [b]) => COLLATOR.compare(a, b))
    .map(([tagValue, bucketQuestions]) => ({ tagValue, questions: bucketQuestions }));

  return { groups, ungrouped };
}

/** Questions with any non-empty main response — feeds the
 *  `answered/total` count in each group's section header. */
export function countAnsweredQuestions(
  questions: readonly QuestionForAnsweredCount[],
): number {
  return questions.filter((q) => {
    const result = q.responseDetails?.mainQuestionResponse?.responseResult;
    return typeof result === 'string' && result !== '';
  }).length;
}
